import { type ScoreCriterion, type SeedAnswers, bucketIndexFor } from '@parcel/filters';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from './api.js';

export type ScoreEntity = 'account' | 'project' | 'contact';
export type ScoreTemplateKey = 'icp' | 'atlas' | 'prospect';
export const SCORE_DATASETS = ['accounts', 'projects', 'contacts'] as const;
export type ScoreDataset = (typeof SCORE_DATASETS)[number];
export const TEMPLATE_KEY_BY_DATASET: Record<ScoreDataset, ScoreTemplateKey> = {
  accounts: 'icp',
  projects: 'atlas',
  contacts: 'prospect',
};
export const ENTITY_BY_DATASET: Record<ScoreDataset, ScoreEntity> = {
  accounts: 'account',
  projects: 'project',
  contacts: 'contact',
};
export const DATASET_BY_ENTITY: Record<ScoreEntity, ScoreDataset> = {
  account: 'accounts',
  project: 'projects',
  contact: 'contacts',
};

export interface ScoreBucket {
  key: string;
  label: string;
}
export type { ScoreCriterion };
export interface Score {
  id: string;
  entity: ScoreEntity;
  key: string;
  name: string;
  buckets: ScoreBucket[];
  criteria: ScoreCriterion[];
  version: number;
  is_template: boolean;
  updated_at: string;
}
export interface ScoreValue {
  score: number;
  bucket: string;
  matched: string[];
}
export type RecordScores = Partial<Record<ScoreTemplateKey, ScoreValue | null>>;
export interface ScorePreview {
  total: number;
  /** Per criterion, how many records it matches. */
  criteria?: Array<{ id: string; matches: number }>;
  buckets: Array<{
    key: string;
    label: string;
    count: number;
    samples: Array<{ id: string; name: string | null; score: number }>;
  }>;
}
export interface ScoreDefinitionInput {
  expected_version: number;
  bucket_labels: string[];
  criteria: ScoreCriterion[];
}

const ws = (workspaceId: string) => `workspace_id=${encodeURIComponent(workspaceId)}`;

/** A criterion stored before scores moved to where has no condition; it reads as absent, never as a crash. */
export function readableScore(score: Score): Score {
  if (!Array.isArray(score.criteria)) return score;
  const criteria = (score.criteria as unknown[]).filter(
    (c): c is ScoreCriterion =>
      typeof c === 'object' &&
      c !== null &&
      typeof (c as { where?: { rules?: unknown } }).where === 'object' &&
      Array.isArray((c as { where: { rules?: unknown } }).where?.rules),
  );
  return criteria.length === score.criteria.length ? score : { ...score, criteria };
}

export async function fetchScores(workspaceId: string): Promise<Score[]> {
  return (await apiFetch<{ data: Score[] }>(`/scores?${ws(workspaceId)}`)).data.map(readableScore);
}
export async function fetchScore(workspaceId: string, key: string): Promise<Score> {
  return readableScore(
    await apiFetch<Score>(`/scores/${encodeURIComponent(key)}?${ws(workspaceId)}`),
  );
}
export async function updateScore(
  workspaceId: string,
  key: string,
  input: ScoreDefinitionInput,
): Promise<Score> {
  return readableScore(
    await apiFetch<Score>(`/scores/${encodeURIComponent(key)}?${ws(workspaceId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  );
}
export async function previewScore(
  workspaceId: string,
  key: string,
  input: { criteria: ScoreCriterion[] },
): Promise<ScorePreview> {
  return apiFetch<ScorePreview>(`/scores/${encodeURIComponent(key)}/preview?${ws(workspaceId)}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** The onboarding answers, typed by the route's own schema; the API writes all three scores and recomputes. */
export type SeedScoresInput = SeedAnswers;
export async function seedScores(workspaceId: string, input: SeedScoresInput): Promise<Score[]> {
  return (
    await apiFetch<{ data: Score[] }>(`/scores/seed?${ws(workspaceId)}`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  ).data.map(readableScore);
}

export const scoresQueryKey = (workspaceId: string | undefined) => ['scores', workspaceId] as const;

// Every list, drawer and the Feed carry scores, so a save refetches them.
const RECORD_QUERY_PREFIXES = [
  'accounts',
  'accounts-browse',
  'account-browse-summary',
  'account-detail',
  'projects',
  'projects-browse',
  'project-detail',
  'project-map-summary',
  'contacts',
  'contacts-browse',
  'contact-browse-summary',
  'contact-detail',
  'feed',
] as const;

export function useScores(workspaceId: string | undefined) {
  return useQuery({
    queryKey: scoresQueryKey(workspaceId),
    queryFn: () => fetchScores(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
}

/** One score, read from the workspace's list so the three pages and the tables share one request. */
export function useScore(workspaceId: string | undefined, key: string) {
  const query = useScores(workspaceId);
  return { ...query, data: query.data?.find((score) => score.key === key) };
}

/** Every list, drawer and the Feed refetch in the background once scores change. */
export function invalidateScoredRecords(queryClient: QueryClient) {
  for (const prefix of RECORD_QUERY_PREFIXES) {
    void queryClient.invalidateQueries({ queryKey: [prefix] });
  }
}

export function useUpdateScore(workspaceId: string | undefined, key: string) {
  const queryClient = useQueryClient();
  return useMutation<Score, ApiError, ScoreDefinitionInput>({
    mutationFn: (input) => updateScore(workspaceId as string, key, input),
    // The save ends when the score is read back.
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: scoresQueryKey(workspaceId) });
      invalidateScoredRecords(queryClient);
    },
  });
}

export function useSeedScores(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<Score[], ApiError, SeedScoresInput>({
    mutationFn: (input) => seedScores(workspaceId as string, input),
    // The response is all three scores; a read already in flight is dropped so it cannot land over them.
    onSuccess: async (scores) => {
      await queryClient.cancelQueries({ queryKey: scoresQueryKey(workspaceId) });
      queryClient.setQueryData(scoresQueryKey(workspaceId), scores);
      invalidateScoredRecords(queryClient);
    },
  });
}

export function usePreviewScore(workspaceId: string | undefined, key: string) {
  return useMutation<ScorePreview, ApiError, { criteria: ScoreCriterion[] }>({
    mutationFn: (input) => previewScore(workspaceId as string, key, input),
  });
}

export function bucketLabel(score: Pick<Score, 'buckets'> | undefined, bucketKey: string): string {
  return score?.buckets.find((b) => b.key === bucketKey)?.label ?? bucketKey;
}

/** The bucket's position, top first; an unknown key counts as the bottom. */
export function bucketIndex(
  score: Pick<Score, 'buckets'> | undefined,
  bucketKey: string,
): 0 | 1 | 2 | 3 {
  const index = score?.buckets.findIndex((b) => b.key === bucketKey) ?? -1;
  return (index < 0 ? 3 : Math.min(index, 3)) as 0 | 1 | 2 | 3;
}

/** A value's bucket position: by its key once the score has loaded, else by the shared thresholds, so nothing flashes as the bottom bucket. */
export function valueBucketIndex(
  score: Pick<Score, 'buckets'> | undefined,
  value: Pick<ScoreValue, 'score' | 'bucket'>,
): 0 | 1 | 2 | 3 {
  return score ? bucketIndex(score, value.bucket) : bucketIndexFor(value.score);
}

/** The save lost a race: someone changed the score since it was loaded. */
export function isVersionConflict(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409
  );
}
